import classNames from 'classnames'
import type { FC } from 'react'
import React, { useCallback } from 'react'
import type { LinkProps } from 'react-router-dom'

import { useTabsContext } from './tabs-context'
import type { TabProps, TabTypeMap } from './types'
import { TAB_THEME_KEY } from './types'
import type { Theme, ThemeUICSSObject } from '../../theme-provider/types'
import { isNotEmptyObject } from '../../utils/assertion'
import { EMPTY_OBJECT, omit, pick } from '../../utils/object'
import {
  getLayout,
  getPaddingX,
  getPaddingY,
  omitLayout,
  omitPaddingX,
  omitPaddingY
} from '../../utils/props-utils'
import Box from '../_primitives/box/box'
import { ButtonPrivate } from '../button/button'

export const TabPrivate = React.forwardRef<
  HTMLButtonElement,
  TabTypeMap['props'] & {
    theme?: Theme
    to?: LinkProps['to']
  }
>(function TabPrivate(props, ref) {
  const {
    value,
    label,
    selected,
    disabled,
    variant = 'tab',
    className,
    onChange,
    onClick,
    onFocus,
    selectionFollowsFocus,
    orientation,
    indicatorPosition,
    iconBefore,
    iconAfter,
    sx = EMPTY_OBJECT as ThemeUICSSObject,
    ...rest
  } = props
  const { variant: tabVariant } = useTabsContext()

  // remove size from layout fields
  const combineLayout = omit(
    {
      ...(orientation === 'vertical' ? {} : { height: '100%' }), // default height for tabs button
      ...getLayout(rest),
      ...getLayout(sx)
    },
    ['size']
  )
  const combinePadding = {
    ...getPaddingX(rest),
    ...getPaddingX(sx),
    ...getPaddingY(rest),
    ...getPaddingY(sx)
  }
  const paddingProps = isNotEmptyObject(combinePadding)
    ? combinePadding
    : {
        ...(orientation === 'horizontal'
          ? {
              emPaddingX: 0
            }
          : {
              emPaddingY: 0
            })
      }

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onChange && value !== undefined) {
        onChange(event, { value, label, icon: iconBefore })
      }

      if (onClick) {
        onClick(event)
      }
    },
    [label, onChange, onClick, value, iconBefore]
  )

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      if (
        selectionFollowsFocus &&
        !selected &&
        onChange &&
        value !== undefined
      ) {
        onChange(event, { value, label, icon: iconBefore })
      }

      if (onFocus) {
        onFocus(event)
      }
    },
    [
      onFocus,
      onChange,
      selectionFollowsFocus,
      selected,
      value,
      label,
      iconBefore
    ]
  )

  // eslint-disable-next-line no-console
  // console.log(`tab render value ${value}`)

  const tab = (
    <ButtonPrivate
      ref={ref}
      role="tab"
      variant={tabVariant ? `${tabVariant}.${variant}` : variant}
      __themeKey={TAB_THEME_KEY}
      className={classNames(
        {
          'csg-tui-tab': true,
          'is-selected': selected,
          'is-disabled': disabled
        },
        className
      )}
      iconAfter={iconAfter}
      aria-selected={selected}
      onClick={!disabled ? handleClick : undefined}
      onFocus={!disabled ? handleFocus : undefined}
      disabled={disabled}
      tabIndex={selected && !disabled ? 0 : -1}
      iconBefore={iconBefore}
      __css={{
        ...paddingProps,
        ...combineLayout
      }}
      sx={omitPaddingX(omitPaddingY(omitLayout(sx)))}
      {...{
        ...omitPaddingX(omitPaddingY(omitLayout(rest))),
        ...pick(rest, ['size']),
        // default reset fontSize to button in order to ignore default size of button component
        ...('size' in rest || 'fontSize' in rest ? {} : { fontSize: '100%' })
      }}
    />
  )

  return orientation === 'vertical' ? (
    <Box
      className="csg-tui-tab-wrapper"
      emPaddingRight="xxl"
      emPaddingLeft={indicatorPosition === 'left' ? 'lg' : 'md'}
    >
      {tab}
    </Box>
  ) : (
    tab
  )
})

const Tab: FC<TabProps> = React.forwardRef<
  HTMLButtonElement,
  TabProps & {
    theme?: Theme
  }
>(function Tab(props, ref) {
  return <TabPrivate ref={ref} {...props} />
})

export default Tab
